import { User, UserRole } from '@/types/global.types';
import { LoginCredentials, AuthResponse, ChangePasswordCredentials } from '../types/auth.types';
import { API_BASE } from '@/utils/api';

// Map backend role to frontend UserRole enum
// Backend and Frontend now use matching role names:
// SUPER_ADMIN, DIRECTOR_GENERAL, BRANCH_MANAGER, PEDAGOGICAL_DIRECTOR, FINANCIAL_MANAGER,
// MARKETING_MANAGER, COMMERCIAL_MANAGER, PROGRAM_HEAD, ACADEMIC_COORDINATOR, TEACHER, STUDENT, PARENT
const mapBackendRole = (role: string): UserRole => {
  const roleMap: Record<string, UserRole> = {
    SUPER_ADMIN: UserRole.SUPER_ADMIN,
    DIRECTOR_GENERAL: UserRole.DIRECTOR_GENERAL,
    BRANCH_MANAGER: UserRole.BRANCH_MANAGER,
    PEDAGOGICAL_DIRECTOR: UserRole.PEDAGOGICAL_DIRECTOR,
    FINANCIAL_MANAGER: UserRole.FINANCIAL_MANAGER,
    MARKETING_MANAGER: UserRole.MARKETING_MANAGER,
    COMMERCIAL_MANAGER: UserRole.COMMERCIAL_MANAGER,
    PROGRAM_HEAD: UserRole.PROGRAM_HEAD,
    ACADEMIC_COORDINATOR: UserRole.ACADEMIC_COORDINATOR,
    TEACHER: UserRole.TEACHER,
    STUDENT: UserRole.STUDENT,
    PARENT: UserRole.PARENT,
  };
  return roleMap[role] || UserRole.STUDENT;
};

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        // Support both email and username login
        ...(credentials.email ? { email: credentials.email } : {}),
        ...(credentials.username ? { username: credentials.username } : {}),
        password: credentials.password,
        // SECURITY: Send hostname for tenant validation
        // This ensures users can only login from their own institution's URL
        hostname: window.location.hostname,
        // For dev mode: pass explicit tenantId from ?tenant= param
        ...(credentials.tenantId ? { tenantId: credentials.tenantId } : {}),
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erreur de connexion' }));
      throw new Error(error.message || 'Identifiants incorrects');
    }

    const data = await response.json();

    // Transform backend response to frontend format
    const user: User = {
      id: data.user.id,
      email: data.user.email,
      username: data.user.username,
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      role: mapBackendRole(data.user.role),
      status: data.user.status, // Include user status (ACTIVE, SUSPENDED, etc.)
      tenantId: data.user.tenantId,
      tenantName: data.user.tenantName,
      tenantLogoUrl: data.user.tenantLogoUrl,
      avatarUrl: undefined,
      isActive: true,
      lastLoginAt: new Date().toISOString(),
      mustChangePassword: data.user.mustChangePassword || false,
      profileCompleted: data.user.profileCompleted || false,
      profileSubmissionStatus: data.user.profileSubmissionStatus,
      // Profile deadline tracking
      isProfileDeadlineBlocked: data.user.isProfileDeadlineBlocked || false,
      profileDeadlineDate: data.user.profileDeadlineDate || null,
      profileDaysRemaining: data.user.profileDaysRemaining ?? null,
      // Student-specific
      academicYearId: data.user.academicYearId || null,
      // Solutions enabled for this tenant
      enabledSolutions: data.user.enabledSolutions || [],
      // Tenant status (for checking suspension)
      tenantStatus: data.user.tenantStatus || undefined,
      // Staff with teaching activity
      isAlsoProfessor: data.user.isAlsoProfessor || false,
      // Job function from profile
      fonction: data.user.fonction || undefined,
    };

    return {
      user,
      tokens: {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken || data.accessToken, // Use same if no refresh token
      },
      mustChangePassword: data.user.mustChangePassword || false,
    };
  },

  async changePassword(credentials: ChangePasswordCredentials, token: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        newPassword: credentials.newPassword,
        confirmPassword: credentials.confirmPassword,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erreur lors du changement de mot de passe' }));
      throw new Error(error.message || 'Erreur lors du changement de mot de passe');
    }

    return response.json();
  },

  async refreshToken(refreshToken: string): Promise<{ accessToken: string; refreshToken: string }> {
    if (!refreshToken) {
      throw new Error('Token de rafraîchissement invalide');
    }

    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Session expirée. Veuillez vous reconnecter.');
    }

    const data = await response.json();

    // Update stored tokens
    this.saveTokens({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    });

    return data;
  },

  async logout(): Promise<void> {
    // Clear local storage - backend doesn't have logout endpoint yet
  },

  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/institutions/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erreur lors de la demande de réinitialisation' }));
      throw new Error(error.message || 'Erreur lors de la demande de réinitialisation');
    }

    return response.json();
  },

  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    if (!token || token.length < 10) {
      throw new Error('Token de réinitialisation invalide ou expiré');
    }
    if (newPassword.length < 8) {
      throw new Error('Le mot de passe doit contenir au moins 8 caractères');
    }

    const response = await fetch(`${API_BASE}/institutions/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, password: newPassword }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erreur lors de la réinitialisation' }));
      throw new Error(error.message || 'Erreur lors de la réinitialisation');
    }

    return response.json();
  },

  async changePasswordSecure(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
    token: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/auth/change-password-secure`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        currentPassword,
        newPassword,
        confirmPassword,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erreur lors du changement de mot de passe' }));
      throw new Error(error.message || 'Erreur lors du changement de mot de passe');
    }

    return response.json();
  },

  async getCurrentUser(token: string): Promise<User> {
    if (!token) {
      throw new Error('Non authentifié');
    }

    const response = await fetch(`${API_BASE}/auth/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Non authentifié');
    }

    const data = await response.json();
    return {
      id: data.id,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      role: mapBackendRole(data.role),
      tenantId: data.tenantId,
      tenantName: undefined,
      avatarUrl: data.avatarUrl,
      isActive: true,
      lastLoginAt: new Date().toISOString(),
    };
  },

  // Storage helpers
  saveTokens(tokens: { accessToken: string; refreshToken?: string }): void {
    localStorage.setItem('nexedu_access_token', tokens.accessToken);
    if (tokens.refreshToken) {
      localStorage.setItem('nexedu_refresh_token', tokens.refreshToken);
    }
  },

  getAccessToken(): string | null {
    return localStorage.getItem('nexedu_access_token');
  },

  getRefreshToken(): string | null {
    return localStorage.getItem('nexedu_refresh_token');
  },

  clearTokens(): void {
    localStorage.removeItem('nexedu_access_token');
    localStorage.removeItem('nexedu_refresh_token');
    localStorage.removeItem('nexedu_user');
    localStorage.removeItem('nexedu_view_as_role');
    // Also clear the zustand persisted auth storage
    localStorage.removeItem('nexedu-auth-storage');
  },

  saveUser(user: User): void {
    localStorage.setItem('nexedu_user', JSON.stringify(user));
  },

  getSavedUser(): User | null {
    const saved = localStorage.getItem('nexedu_user');
    if (saved) {
      try {
        return JSON.parse(saved) as User;
      } catch {
        return null;
      }
    }
    return null;
  },
};
